import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {
  AddDocument,
  DeleteResources,
  DocumentAuthor,
  DocumentDetails,
  DocumentFull,
  DocumentLinkedElement,
  DocumentsListFiltersWithPage,
  DocumentsPaginated,
  MoveDocuments,
  PackResources,
  PackResourcesToZipStatusResult,
  UpdateDocument
} from '@simOn/documents/element/models';
import { BuildHttpParamsFromFilters } from '@simOn/utils/helpers';
import { API_URL } from '@simOn/utils/tokens';
import { Observable } from 'rxjs';

/**
 * Data access for `/api/Documents/*`.
 * All calls rely on the ambient `apartmentId` header injected by
 * `ApartmentIdApiInterceptor` (set via `SpaceFacade.setSelectedId`).
 */
@Injectable({ providedIn: 'root' })
export class DocumentsApiService {
  private readonly http = inject(HttpClient);
  private readonly _apiUrl = inject(API_URL);

  getDocuments(filters: Partial<DocumentsListFiltersWithPage>): Observable<DocumentsPaginated> {
    const params = BuildHttpParamsFromFilters({
      PageNumber: filters.pageNumber,
      PageSize: filters.pageSize,
      Name: filters.name,
      UnassignedOnly: filters.unassignedOnly,
      DocumentTypes: filters.documentTypes,
      AuthorIds: filters.authorIds,
      DirectorySearchLevel: filters.directorySearchLevel,
      SortBy: filters.sortBy,
      IsAscending: filters.isAscending,
      directoryId: filters.directoryId
    });
    return this.http.get<DocumentsPaginated>(`${this._apiUrl}/Documents/GetDocuments`, { params });
  }

  getDocument(documentId: string): Observable<DocumentFull> {
    return this.http.get<DocumentFull>(`${this._apiUrl}/Documents/GetDocument`, {
      params: { documentId }
    });
  }

  getResourcesLinkedElements(documentIds: string[], directoryIds: string[]): Observable<DocumentLinkedElement[]> {
    return this.http.post<DocumentLinkedElement[]>(`${this._apiUrl}/Documents/GetResourcesLinkedElements`, {documentIds, directoryIds});
  }

  getDocumentsAuthors(): Observable<DocumentAuthor[]> {
    return this.http.get<DocumentAuthor[]>(`${this._apiUrl}/Documents/GetDocumentsAuthors`);
  }

  createDocument(body: AddDocument): Observable<DocumentDetails> {
    return this.http.post<DocumentDetails>(`${this._apiUrl}/Documents/CreateDocument`, body);
  }

  updateDocument(body: UpdateDocument): Observable<DocumentDetails> {
    return this.http.put<DocumentDetails>(`${this._apiUrl}/Documents/UpdateDocument`, body);
  }

  moveResources(body: MoveDocuments): Observable<void> {
    return this.http.put<void>(`${this._apiUrl}/Documents/MoveResources`, body);
  }

  deleteResources({ documentIds, directoryIds }: DeleteResources): Observable<void> {
    const params = BuildHttpParamsFromFilters({
      DocumentIds: documentIds,
      DirectoryIds: directoryIds
    });
    return this.http.delete<void>(`${this._apiUrl}/Documents/DeleteResources`, { params });
  }

  packDocuments(body: PackResources): Observable<string> {
    return this.http.post<string>(`${this._apiUrl}/Documents/PackDocuments`, body);
  }

  getPackStatus(taskId: string): Observable<PackResourcesToZipStatusResult> {
    return this.http.get<PackResourcesToZipStatusResult>(`${this._apiUrl}/Documents/GetPackDocumentsToZipStatus`, {
      params: { taskId }
    });
  }
}
